import React, { useState } from 'react';
import {
    Box,
    Grid,
    Paper,
    TextField,
    IconButton,
    Typography,
    Button,
    Checkbox,
    FormControlLabel,
    Card,
    CardContent,
    CardMedia,
    CardActions,
} from '@mui/material';
import { FilterList, ShoppingCartOutlined, Tune, SearchOutlined } from '@mui/icons-material';
import { useLocation } from 'react-router';

// Define types for selected filters
type SelectedFilters = {
    categories: string[];
    gender: string[];
};

const ProductPage = () => {
    const location = useLocation()

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({
        categories: [],
        gender: [],
    });
    console.log("location", location)

    const products = [
        { title: 'Canon EOS 1500D', price: 12.99, discount: 15.99, image: 'https://images.unsplash.com/photo-1498125552200-5984fc32f83c?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D', category: 'Electronics' },
        { title: 'Apple MacBook Pro', price: 14.59, discount: 0, image: 'https://plus.unsplash.com/premium_photo-1681566925324-ee1e65d9d53e?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8bWFjYm9va2NhcmQlMjBpbWFnZXN8ZW58MHx8MHx8fDA%3D', category: 'Electronics' },
        { title: 'Luxury Watches', price: 29.99, discount: 36.0, image: 'https://images.unsplash.com/photo-1493662042295-319935c83112?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8N3x8d2F0Y2hlcyUyMGltYWdlc3xlbnwwfHwwfHx8MA%3D%3D', category: 'Fashion' },
        { title: 'Fitbit MX30', price: 49.9, discount: 85.0, image: 'https://images.unsplash.com/photo-1510017803434-a899398421b3?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MXx8Rml0Yml0JTIwTVgzMCUyMGltYWdlc3xlbnwwfHwwfHx8MA%3D%3D', category: 'Electronics' },
        { title: 'Fiction Book', price: 8.99, discount: 12.0, image: 'https://plus.unsplash.com/premium_photo-1731951687922-1bb9d7722a49?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MXx8RmljdGlvbiUyMEJvb2slMjBpbWFnZXN8ZW58MHx8MHx8fDA%3D', category: 'Books' },
    ];


    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
    };

    const handleFilterChange = (filterType: keyof SelectedFilters, value: string) => {
        setSelectedFilters((prev) => {
            const updatedFilters = { ...prev };
            if (updatedFilters[filterType].includes(value)) {
                updatedFilters[filterType] = updatedFilters[filterType].filter((item) => item !== value);
            } else {
                updatedFilters[filterType].push(value);
            }
            return updatedFilters;
        });
    };

    const filteredProducts = products.filter((product) => {
        const matchesSearch =
            product.title.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory =
            selectedFilters.categories.length === 0 ||
            selectedFilters.categories.includes('All') ||
            selectedFilters.categories.includes(product.category);

        return matchesSearch && matchesCategory;
    });

    return (
        <Box sx={{ padding: 3, bgcolor: '#f4f6f8' }}>
            {/* Top Bar */}
            <Paper
                sx={{
                    padding: '8px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 2,
                }}
                elevation={1}
            >
                <TextField
                    placeholder="Search Product"
                    variant="outlined"
                    size="small"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    sx={{ width: '40%' }}
                    InputProps={{
                        endAdornment: (
                            <IconButton>
                                <SearchOutlined />
                            </IconButton>
                        ),
                    }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button startIcon={<FilterList />} variant="outlined">
                        Filter
                    </Button>
                    <Button startIcon={<Tune />} variant="outlined">
                        Sort: Price Low to High
                    </Button>
                </Box>
            </Paper>

            {/* Content */}
            <Grid container spacing={3}>
                {/* Filters Section */}
                <Grid item xs={12} md={3}>
                    <Paper sx={{ padding: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Gender
                        </Typography>
                        <FormControlLabel control={<Checkbox />} label="Male" />
                        <FormControlLabel control={<Checkbox />} label="Female" />
                        <FormControlLabel control={<Checkbox />} label="Kids" />

                        <Typography variant="h6" sx={{ marginTop: 3 }} gutterBottom>
                            Categories
                        </Typography>
                        {['All', 'Electronics', 'Books', 'Fashion'].map((category) => (
                            <FormControlLabel
                                key={category}
                                control={
                                    <Checkbox
                                        checked={selectedFilters.categories.includes(category)}
                                        onChange={() => handleFilterChange('categories', category)}
                                    />
                                }
                                label={category}
                            />
                        ))}
                    </Paper>
                </Grid>

                {/* Product Cards Section */}
                <Grid item xs={12} md={9}>
                    <Grid container spacing={3}>
                        {filteredProducts.map((product, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Card sx={{
                                    height: '100%',
                                    borderRadius: 2,
                                    boxShadow: 3,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': {
                                        transform: 'scale(1.05)',
                                        boxShadow: 6,
                                    },
                                }}>
                                    <CardMedia
                                        component="img"
                                        height="200"
                                        image={product.image}
                                        alt={product.title}
                                        sx={{
                                            borderTopLeftRadius: 2,
                                            borderTopRightRadius: 2,
                                            objectFit: 'cover',
                                        }}
                                    />
                                    <CardContent sx={{ padding: 2 }}>
                                        <Typography variant="h6" fontWeight={600} sx={{ marginBottom: 1 }}>
                                            {product.title}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                                                ${product.price}
                                            </Typography>
                                            {product.discount > 0 && (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        textDecoration: 'line-through',
                                                        color: 'text.secondary',
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    ${product.discount}
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* Description */}
                                        <Typography variant="body2" color="text.secondary" sx={{ marginTop: 1, height: '50px', overflow: 'hidden' }}>
                                            This is a short description of the product. It's a great item for anyone looking to get a high-quality product at a great price.
                                        </Typography>

                                        {/* Rating */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: 1 }}>
                                            <Typography variant="body2" color="text.primary">
                                                ★★★★★
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                (4.5)
                                            </Typography>
                                        </Box>

                                        {/* Buyers */}
                                        <Typography variant="body2" color="text.secondary" sx={{ marginTop: 1 }}>
                                            50k+ Buyers
                                        </Typography>
                                    </CardContent>
                                    <CardActions sx={{ padding: 1.5 }}>
                                        <Button
                                            startIcon={<ShoppingCartOutlined />}
                                            size="small"
                                            variant="contained"
                                            fullWidth
                                            color="primary"
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 600,
                                            }}
                                        >
                                            Add to Cart
                                        </Button>
                                    </CardActions>
                                </Card>

                            </Grid>
                        ))}
                    </Grid>
                </Grid>

            </Grid>

            {/* Floating Settings Button */}
            <IconButton
                sx={{
                    position: 'fixed',
                    bottom: 16,
                    right: 16,
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                }}
            >
                <Tune />
            </IconButton>
        </Box>
    );
};

export default ProductPage;
